import React, { useEffect, useMemo, useRef, useState } from 'react';
import DisabledFieldset from '../../common/DisabledFieldset';
import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import {
  Box,
  Typography,
  Grid,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Divider,
  Alert,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListSubheader,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getDisplayColumnPrefix, getPeriodType } from '../../../utils/periodUtils';
import { authenticatedFetch } from '../../../services/api_utils';
import { functionEndpoint } from '../../../services/endpoints';
import DeleteResourceDialog from '../../common/DeleteResourceDialog';
import useDeleteConfirmation from '../../common/useDeleteConfirmation';

function flattenValueLeaves(schema) {
  const out = [];
  if (!schema || typeof schema !== 'object') return out;
  Object.entries(schema).forEach(([k, v]) => {
    const isLeaf = v && typeof v === 'object' && (
      Object.prototype.hasOwnProperty.call(v, 'updateBehavior') ||
      Object.prototype.hasOwnProperty.call(v, 'type')
    );
    if (isLeaf) {
      out.push(k);
    } else {
      const nested = (v && typeof v === 'object' && v.schema && typeof v.schema === 'object') ? v.schema : (v || {});
      Object.keys(nested).forEach(sub => out.push(`${k}.${sub}`));
    }
  });
  return out;
}

function flattenTemplateLeaves(templateSchema) {
  // Template schema mirrors cleaned sheet: { columns: [{ name, children: [...] }] }
  const out = [];
  function dfs(node, path) {
    const label = node.name || '';
    const next = label ? (path ? `${path} > ${label}` : label) : path;
    const children = Array.isArray(node.children) ? node.children : [];
    if (!children.length) {
      if (next) out.push(next);
      return;
    }
    children.forEach(child => dfs(child, next));
  }
  const cols = (templateSchema && templateSchema.columns) || [];
  cols.forEach(col => dfs(col, ''));
  return out;
}

// The mapping editor's draft is part of the settings page's save and dirty
// model: `onDraftDirtyChange(dirty)` lets the page flag its table dirty (so the
// Save / Discard / Stay dialog and the leave guard cover the draft), and
// `draftRef.current` gives the page `validate / save / discard` so the
// page-level Save Settings writes the mapping too.
const draftSignature = (editingId, templateId, name, mapping) => JSON.stringify([
  editingId,
  templateId || null,
  (name || '').trim(),
  Object.keys(mapping || {}).sort().filter((key) => mapping[key] !== '').map((key) => [key, mapping[key]]),
]);

export default function ExportSettings({ companyId, table, disabled = false, draftRef = null, onDraftDirtyChange = null }) {
  // Existing state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateDocsById, setTemplateDocsById] = useState({});
  const [mapping, setMapping] = useState({});
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [existingMappings, setExistingMappings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [mappingName, setMappingName] = useState('');
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [deletingMapping, setDeletingMapping] = useState(false);
  const [mappingDeleteError, setMappingDeleteError] = useState(null);

  // New state for custom templates
  const [customTemplates, setCustomTemplates] = useState([]);
  // The template and mapping lists start empty; neither reads as "none" until
  // its read has returned.
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [mappingsLoading, setMappingsLoading] = useState(true);
  // A read that failed: the list is unknown, not empty, and says so.
  const [templatesError, setTemplatesError] = useState(false);
  const [mappingsError, setMappingsError] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFirstHeaderRow, setUploadFirstHeaderRow] = useState(1);
  const [uploadLastHeaderRow, setUploadLastHeaderRow] = useState(1);
  const [uploadTimePeriod, setUploadTimePeriod] = useState('any');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const templateDeletion = useDeleteConfirmation({
    resourceType: 'custom_template',
    onPreviewError: (e) => setError(e.message || 'Failed to check template dependencies'),
    onDeleteError: (e) => setError(e.message),
    onDeleted: async (template) => {
      setCustomTemplates(prev => prev.filter(t => t.id !== template.id));
      setTemplateDocsById(prev => {
        const newById = { ...prev };
        delete newById[`custom_${template.id}`];
        return newById;
      });
      if (selectedTemplateId === `custom_${template.id}`) {
        setSelectedTemplateId(null);
        setMapping({});
        setMappingName('');
        setEditingMappingId(null);
      }
      // Committed: the confirmation has already let go of the template, so the
      // saved-format refresh below can fail on its own without the dialog
      // re-offering a template that no longer exists.
      try {
        const formatsSnap = await getDocs(collection(db, `users/${companyId}/tables/${tableId}/export_formats`));
        const formats = formatsSnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
        setExistingMappings(formats);
      } catch (refreshErr) {
        console.error('Failed to refresh export formats after a template delete:', refreshErr);
        setError(`The template was deleted, but the saved formats could not be refreshed: ${refreshErr.message}. Refresh the page to see the current list.`);
      }
    },
  });

  // The draft as it stood when the editor opened; the draft is dirty once it differs.
  const draftBaselineRef = useRef(null);
  // The id a new mapping will be written under, fixed for the life of the draft (M47).
  const newFormatIdRef = useRef(null);

  const tableId = table?.id;
  const tablePeriodType = getPeriodType(table);

  // Fetch both global and custom templates. Each read belongs to the table it
  // was started for: a response arriving after the table (or company) changed
  // is dropped, so a slow read for the previous table can never populate this
  // one, and the lists start empty and loading rather than showing the last
  // table's until the new read lands.
  useEffect(() => {
    let cancelled = false;
    async function fetchAllTemplates() {
      // Fetch global templates
      const globalSnap = await getDocs(collection(db, 'excel_templates'));
      const globalList = [];
      const byId = {};
      globalSnap.docs.forEach(d => {
        const data = d.data();
        const template = { id: d.id, ...data, source: 'global' };
        globalList.push(template);
        byId[d.id] = template;
      });

      // Fetch custom templates for this company
      const customSnap = await getDocs(collection(db, `users/${companyId}/excel_templates`));
      const customList = [];
      customSnap.docs.forEach(d => {
        const data = d.data();
        const template = { id: d.id, ...data, source: 'custom' };
        customList.push(template);
        byId[`custom_${d.id}`] = template;
      });

      // Filter by table period type and template type
      const filterTemplate = (t) => {
        if (t.public === false && t.source === 'global') return false;
        if (t.type !== 'per_time_period') return false;
        const templatePeriod = t.time_period || 'any';
        if (templatePeriod === 'any') return true;
        return templatePeriod === tablePeriodType;
      };

      if (cancelled) return;
      setTemplates(globalList.filter(filterTemplate));
      setCustomTemplates(customList.filter(filterTemplate));
      setTemplateDocsById(byId);
    }

    setTemplates([]);
    setCustomTemplates([]);
    setTemplateDocsById({});
    setTemplatesError(false);
    setTemplatesLoading(true);
    fetchAllTemplates()
      .catch((e) => {
        console.error('Failed to load export templates:', e);
        if (!cancelled) setTemplatesError(true);
      })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, tablePeriodType]);

  useEffect(() => {
    let cancelled = false;
    async function fetchActive() {
      if (!companyId || !tableId) return;
      const formatsSnap = await getDocs(collection(db, `users/${companyId}/tables/${tableId}/export_formats`));
      if (cancelled) return;
      const fmts = formatsSnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      setExistingMappings(fmts);
    }
    setExistingMappings([]);
    setMappingsError(false);
    setMappingsLoading(true);
    fetchActive()
      .catch((e) => {
        console.error('Failed to load export formats:', e);
        if (!cancelled) setMappingsError(true);
      })
      .finally(() => { if (!cancelled) setMappingsLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, tableId]);

  // Combined template list for selection
  const allTemplates = useMemo(() => {
    return [
      ...templates.map(t => ({ ...t, displaySource: 'Global' })),
      ...customTemplates.map(t => ({ ...t, displaySource: 'Custom' }))
    ];
  }, [templates, customTemplates]);

  const templateLeafOptions = useMemo(() => {
    if (!selectedTemplateId) return [];
    const tpl = templateDocsById[selectedTemplateId];
    return tpl ? flattenTemplateLeaves(tpl.schema) : [];
  }, [selectedTemplateId, templateDocsById]);

  const tableFieldOptions = useMemo(() => {
    if (!table) return [];
    const out = [];

    if (table.identifier?.name) out.push(table.identifier.name);
    (table.attributeColumns || []).forEach(c => c?.name && out.push(c.name));
    (table.tableAttributeColumns || []).forEach(c => c?.name && out.push(c.name));
    (table.detailColumns || []).forEach(c => c?.name && out.push(c.name));

    // Only a time-series table has metrics, each under its period prefix.
    if (table.tableType === 'timeSeries') {
      const periodPrefix = getDisplayColumnPrefix(table);
      flattenValueLeaves(table.metricColumns?.schema || {}).forEach(k => out.push(`${periodPrefix}.${k}`));
      out.push(periodPrefix);
    }

    return out;
  }, [table]);

  function generateId(len = 8) {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let out = '';
    try {
      const arr = new Uint32Array(len);
      (window.crypto || window.msCrypto).getRandomValues(arr);
      for (let i = 0; i < len; i += 1) out += chars[arr[i] % chars.length];
      return out;
    } catch (e) {
      for (let i = 0; i < len; i += 1) out += chars[Math.floor(Math.random() * chars.length)];
      return out;
    }
  }

  // Helper to get template_ref_path based on source
  const getTemplateRefPath = (templateId, source) => {
    if (source === 'custom') {
      return `users/${companyId}/excel_templates/${templateId}`;
    }
    return `excel_templates/${templateId}`;
  };

  const editorOpen = editingMappingId !== null || Boolean(selectedTemplateId);
  const draftDirty = editorOpen
    && draftBaselineRef.current !== null
    && draftSignature(editingMappingId, selectedTemplateId, mappingName, mapping) !== draftBaselineRef.current;

  useEffect(() => {
    onDraftDirtyChange?.(draftDirty);
  }, [draftDirty, onDraftDirtyChange]);
  useEffect(() => () => { onDraftDirtyChange?.(false); }, [onDraftDirtyChange]);

  // The first reason the draft cannot be written, or null. Shared by the tab's own
  // Save Mapping and the page-level Save Settings, so both refuse the same drafts.
  const validateDraft = () => {
    if (!companyId || !tableId) return 'Missing company or table';
    if (!selectedTemplateId) return 'Select a template for the export mapping';
    if (!(mappingName || '').trim()) return 'Enter a name for the export mapping';
    return null;
  };

  const closeEditor = () => {
    setEditingMappingId(null);
    setSelectedTemplateId(null);
    setMapping({});
    setMappingName('');
    draftBaselineRef.current = null;
    newFormatIdRef.current = null;
  };

  // Write the draft as an export_formats document and close the editor. Throws
  // with a user-facing message; the callers decide where that message shows.
  const saveDraft = async () => {
    const validationError = validateDraft();
    if (validationError) throw new Error(validationError);
    const nameTrimmed = (mappingName || '').trim();

    // Find the selected template to get its source
    const selectedTemplate = allTemplates.find(t =>
      (t.source === 'custom' ? `custom_${t.id}` : t.id) === selectedTemplateId
    );

    if (!selectedTemplate) throw new Error('Template not found');

    const actualTemplateId = selectedTemplate.id;
    const templateRefPath = getTemplateRefPath(actualTemplateId, selectedTemplate.source);

    // Use the existing id when editing. A new draft keeps one id for its lifetime, so a
    // retry after a failed write lands on the same document instead of a duplicate (M47).
    const isNewDraft = !editingMappingId || editingMappingId === 'new';
    if (isNewDraft && !newFormatIdRef.current) newFormatIdRef.current = generateId(8);
    const exportFormatId = isNewDraft ? newFormatIdRef.current : editingMappingId;
    const payload = {
      template_id: actualTemplateId,
      template_ref_path: templateRefPath,
      template_type: 'per_time_period',
      name: nameTrimmed,
      mapping,
    };
    const fmtRef = doc(db, `users/${companyId}/tables/${tableId}/export_formats/${exportFormatId}`);
    // Replace, never merge. `payload` carries every field the document holds, and a
    // merge deep-merges map fields: changing the template resets the editor's mapping
    // to {}, which merged into the stored map left every key from the OLD template in
    // place. The export endpoint then resolves the NEW template's leaves against those
    // survivors, so a column the user deliberately left blank was filled from the
    // previous mapping — and the stale keys were invisible in the editor, which renders
    // only the current template's leaves, so they could never be removed.
    await setDoc(fmtRef, payload);
    // The write is the save. The list follows the acknowledged id at once and the editor
    // closes; the reload below only picks up other tabs' changes, so its failure is
    // reported as exactly that and never as a failed save (M47).
    const saved = { id: exportFormatId, ...payload };
    setExistingMappings(prev => (
      prev.some(m => m.id === exportFormatId)
        ? prev.map(m => (m.id === exportFormatId ? { ...m, ...saved } : m))
        : [...prev, saved]
    ));
    closeEditor();
    try {
      const formatsSnap = await getDocs(collection(db, `users/${companyId}/tables/${tableId}/export_formats`));
      const fmts = formatsSnap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }));
      setExistingMappings(fmts);
    } catch (e) {
      console.error('Failed to refresh export formats after save:', e);
      setError(`Saved, but the mapping list could not be refreshed: ${e.message || 'unknown error'}`);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      await saveDraft();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (draftRef) {
    draftRef.current = { dirty: draftDirty, validate: validateDraft, save: saveDraft, discard: closeEditor };
  }

  const beginAddNew = () => {
    // Use a non-null sentinel to trigger editor visibility
    setEditingMappingId('new');
    setSelectedTemplateId(null);
    setMapping({});
    setMappingName('');
    draftBaselineRef.current = draftSignature('new', null, '', {});
  };

  const beginEdit = (m) => {
    setEditingMappingId(m.id);
    // Determine if this mapping uses a custom template
    const refPath = m.template_ref_path || '';
    const isCustom = refPath.startsWith('users/');
    const templateKey = isCustom ? `custom_${m.template_id}` : m.template_id;
    setSelectedTemplateId(templateKey || null);
    setMapping(m.mapping || {});
    setMappingName(m.name || '');
    draftBaselineRef.current = draftSignature(m.id, templateKey || null, m.name || '', m.mapping || {});
  };

  const deleteMapping = async () => {
    if (!mappingToDelete || deletingMapping) return;
    setDeletingMapping(true);
    setMappingDeleteError(null);
    try {
      await deleteDoc(mappingToDelete.reference);
      setExistingMappings(previous => previous.filter(format => format.id !== mappingToDelete.id));
      if (editingMappingId === mappingToDelete.id) closeEditor();
      setMappingToDelete(null);
    } catch (e) {
      setMappingDeleteError(e.message || 'Failed to delete the saved format. Try again.');
    } finally {
      setDeletingMapping(false);
    }
  };

  // Upload custom template handler
  const handleUploadTemplate = async () => {
    if (!uploadFile || !uploadName.trim()) {
      setUploadError('Please provide a file and name');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const reader = new FileReader();

      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const endpoint = functionEndpoint('upload_custom_template');

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          file_base64: base64,
          file_name: uploadFile.name,
          name: uploadName.trim(),
          description: uploadDescription.trim(),
          time_period: uploadTimePeriod,
          first_header_row: uploadFirstHeaderRow,
          last_header_row: uploadLastHeaderRow,
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Upload failed');
      }
    } catch (e) {
      setUploadError(e.message);
      return;
    } finally {
      setUploading(false);
    }

    // The upload is done once the endpoint acknowledges it: the form resets and the success
    // shows now. The template list reload is a separate step, so its failure cannot read as
    // a failed upload and invite a retry that creates a second template (M47).
    setShowUploadForm(false);
    setUploadFile(null);
    setUploadName('');
    setUploadDescription('');
    setUploadFirstHeaderRow(1);
    setUploadLastHeaderRow(1);
    setUploadTimePeriod('any');
    setSuccess(true);
    setTimeout(() => setSuccess(false), 2500);

    try {
      const customSnap = await getDocs(collection(db, `users/${companyId}/excel_templates`));
      const customList = customSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        source: 'custom'
      }));

      // Filter by table period type and template type
      const filterTemplate = (t) => {
        if (t.type !== 'per_time_period') return false;
        const templatePeriod = t.time_period || 'any';
        if (templatePeriod === 'any') return true;
        return templatePeriod === tablePeriodType;
      };

      setCustomTemplates(customList.filter(filterTemplate));
      setTemplateDocsById(prev => {
        const next = { ...prev };
        customList.forEach(t => {
          next[`custom_${t.id}`] = t;
        });
        return next;
      });
    } catch (e) {
      console.error('Failed to refresh custom templates after upload:', e);
      setError(`Template uploaded, but the template list could not be refreshed: ${e.message || 'unknown error'}`);
    }
  };

  const handleOpenDeleteCustomTemplate = (template) => {
    setError(null);
    templateDeletion.open(template);
  };

  return (
    <DisabledFieldset disabled={disabled || saving}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Saved
        </Alert>
      )}

      {/* Custom Templates Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Custom Templates</Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowUploadForm(true)}
            startIcon={<AddIcon />}
          >
            Upload Template
          </Button>
        </Stack>

        {templatesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : templatesError ? (
          <Alert severity="error">The export templates could not be loaded. Reopen this tab to try again.</Alert>
        ) : customTemplates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No custom templates. Upload your own Excel template to get started.
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {customTemplates.map(t => (
              <Grid item xs={12} md={6} key={t.id}>
                <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.time_period} period • headers: rows {t.column_headers?.first_row_index || 1}-{t.column_headers?.last_row_index || t.header_rows || 1}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => handleOpenDeleteCustomTemplate(t)}
                  >
                    Delete
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <DeleteResourceDialog
        open={Boolean(templateDeletion.target)}
        title={`Delete ${templateDeletion.target?.name || templateDeletion.target?.id || 'Custom Template'}`}
        preview={templateDeletion.preview}
        scopeChanged={templateDeletion.scopeChanged}
        executionError={templateDeletion.executionError}
        onConfirm={templateDeletion.confirm}
        onClose={templateDeletion.close}
        busy={templateDeletion.busy}
      />

      {/* Upload Form Dialog */}
      {/* Closing cannot cancel the upload request, so the dialog stays until it settles. */}
      <Dialog open={showUploadForm} onClose={() => { if (!uploading) setShowUploadForm(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Custom Template</DialogTitle>
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" disabled={uploading}>
              {uploadFile ? uploadFile.name : 'Choose Excel File'}
              <input
                type="file"
                hidden
                accept=".xlsx"
                disabled={uploading}
                onChange={(e) => setUploadFile(e.target.files[0])}
              />
            </Button>

            <TextField
              label="Template Name"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Description"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="First Header Row"
                  type="number"
                  value={uploadFirstHeaderRow}
                  onChange={(e) => setUploadFirstHeaderRow(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                  fullWidth
                  helperText="Row number where column headers begin (1-based)"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Last Header Row"
                  type="number"
                  value={uploadLastHeaderRow}
                  onChange={(e) => setUploadLastHeaderRow(parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1 }}
                  fullWidth
                  helperText="Row number where column headers end (1-based)"
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={uploadTimePeriod}
                label="Time Period"
                onChange={(e) => setUploadTimePeriod(e.target.value)}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="day">Daily</MenuItem>
                <MenuItem value="month">Monthly</MenuItem>
                <MenuItem value="quarter">Quarterly</MenuItem>
                <MenuItem value="year">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadForm(false)} disabled={uploading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadTemplate}
            disabled={uploading || !uploadFile || !uploadName.trim()}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Existing Mappings</Typography>
          <Button variant="contained" size="small" onClick={beginAddNew}>Add New Mapping</Button>
        </Stack>
        {mappingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : mappingsError ? (
          <Alert severity="error">The saved mappings could not be loaded. Reopen this tab to try again.</Alert>
        ) : existingMappings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No mappings defined yet.</Typography>
        ) : (
          <Grid container spacing={1}>
            {existingMappings.map(m => (
              <Grid item xs={12} md={6} key={m.id}>
                <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.name || m.id}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Template: {m.template_id}
                      {m.template_ref_path?.startsWith('users/') && ' (Custom)'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => beginEdit(m)}>Edit</Button>
                    <Button size="small" color="error" variant="outlined" onClick={() => {
                      setMappingDeleteError(null);
                      setMappingToDelete({
                        ...m,
                        reference: doc(db, `users/${companyId}/tables/${tableId}/export_formats/${m.id}`),
                      });
                    }}>Delete</Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <Dialog
        open={Boolean(mappingToDelete)}
        onClose={deletingMapping ? undefined : () => setMappingToDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete saved format?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete &quot;{mappingToDelete?.name || mappingToDelete?.id}&quot; and its field mappings?
            This cannot be undone.
          </Typography>
          {mappingDeleteError && <Alert severity="error" sx={{ mt: 2 }}>{mappingDeleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingToDelete(null)} disabled={deletingMapping}>Cancel</Button>
          <Button onClick={deleteMapping} color="error" variant="contained" disabled={deletingMapping}>
            {deletingMapping ? 'Deleting...' : 'Delete saved format'}
          </Button>
        </DialogActions>
      </Dialog>

      {(editingMappingId !== null || selectedTemplateId) && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Mapping Editor</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Name"
                value={mappingName}
                onChange={(e) => setMappingName(e.target.value)}
                placeholder="Enter a name for this mapping"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="template-select-label">Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  value={selectedTemplateId || ''}
                  label="Template"
                  onChange={(e) => {
                    setSelectedTemplateId(e.target.value || null);
                    setMapping({});
                  }}
                >
                  {templates.length > 0 && (
                    <ListSubheader>Global Templates</ListSubheader>
                  )}
                  {templates.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name || t.id}</MenuItem>
                  ))}
                  {customTemplates.length > 0 && (
                    <ListSubheader>Custom Templates</ListSubheader>
                  )}
                  {customTemplates.map(t => (
                    <MenuItem key={`custom_${t.id}`} value={`custom_${t.id}`}>{t.name || t.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Field Mapping</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={1}>
            {templateLeafOptions.map((leaf) => (
              <Grid item xs={12} md={6} key={leaf}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6}>
                    <Typography variant="body2">{leaf}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <Select
                        displayEmpty
                        value={mapping[leaf] || ''}
                        onChange={(e) => setMapping(prev => ({ ...prev, [leaf]: e.target.value }))}
                      >
                        <MenuItem value="">
                          <em>(blank)</em>
                        </MenuItem>
                        {tableFieldOptions.map(opt => (
                          <MenuItem key={opt} value={opt}>{opt.replace(/^period\./, 'period. ')}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button size="small" onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={handleSave} disabled={!selectedTemplateId || saving}>
              {saving ? 'Saving...' : 'Save Mapping'}
            </Button>
          </Box>
        </Paper>
      )}
    </DisabledFieldset>
  );
}
